<template>
  <div class="bg-white pa-5 rounded-lg search-area-container">
    <div class="d-flex justify-space-between align-center mb-4">
      <h3 class="text-text-primary">{{ $t("archives.ArchivesSearchArea.title") }}</h3>
      <app-button :to="{ name: ARCHIVES_ADD_ROUTE_NAME }">
        {{ $t("archives.views.Archives.addButton") }}
      </app-button>
    </div>
    <v-row>
      <v-col lg="4" cols="12">
        <div class="d-flex align-center">
          <AppTextField id="document-name"
                        v-model.trim="search.docName"
                        :placeholder="$t('archives.ArchivesSearchArea.inputDocumentNamePlaceholder')"
                        :label="$t('archives.ArchivesSearchArea.inputDocumentNameLabel')"
          >
            <template #append>
              <AppHelpTooltip :text="$t('archives.ArchivesSearchArea.inputDocumentNameHint')"/>
            </template>
          </AppTextField>
        </div>
      </v-col>
      <v-col lg="4" cols="12">
        <div class="d-flex align-center">
          <AppTextField id="folder-ref"
                        v-model.trim="search.folderRef"
                        :placeholder="$t('archives.ArchivesSearchArea.inputFolderRefPlaceholder')"
                        :label="$t('archives.ArchivesSearchArea.inputFolderRefLabel')"
          >
            <template #append>
              <AppHelpTooltip :text="$t('archives.ArchivesSearchArea.inputFolderRefHint')"/>
            </template>
          </AppTextField>
        </div>
      </v-col>
      <v-col lg="4" cols="12">
        <div class="d-flex align-center">
          <AppTextField id="archiver-name"
                        v-model.trim="search.archiverName"
                        :placeholder="$t('archives.ArchivesSearchArea.inputArchiverNamePlaceholder')"
                        :label="$t('archives.ArchivesSearchArea.inputArchiverNameLabel')"
          >
            <template #append>
              <AppHelpTooltip :text="$t('archives.ArchivesSearchArea.inputArchiverNameHint')"/>
            </template>
          </AppTextField>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col lg="4" cols="12">
        <AppDateRangePicker
          :label="$t('archives.ArchivesSearchArea.inputArchiveDateLabel')"
          :model-value="archiveDateModel"
          @update:model-value=" handleDateRangeChange($event, 'archiveDate')"
        />
      </v-col>
      <v-col v-for="field in additionalFields" :key="field.field" lg="4" cols="12">
        <div class="d-flex align-center">
          <AppTextField
            :id="field.field"
            v-model.trim="search.additionalFields[field.field]"
            :label="field[$i18n.locale]"
          />
        </div>
      </v-col>
      <template v-if="search.signedDocuments">
        <v-col lg="4" cols="12">
          <div class="d-flex align-center">
            <AppTextField id="signers-name"
                          v-model.trim="search.signerName"
                          :placeholder="$t('archives.ArchivesSearchArea.inputSignersNamesPlaceholder')"
                          :label="$t('archives.ArchivesSearchArea.inputSignersNamesLabel')"
            >
              <template #append>
                <AppHelpTooltip :text="$t('archives.ArchivesSearchArea.inputSignersNamesHint')"/>
              </template>
            </AppTextField>
          </div>
        </v-col>
        <v-col lg="4" cols="12">
          <AppDateRangePicker
            :label="$t('archives.ArchivesSearchArea.inputSignDateLabel')"
            :model-value="signDateModel"
            @update:model-value="handleDateRangeChange($event, 'signDate')"
          />
        </v-col>
      </template>
    </v-row>
    <div class="mt-2">
      <app-toggle
        :model-value="search.signedDocuments"
        :label="$t('archives.ArchivesSearchArea.inputSearchSignedDocumentsLabel')"
        @update:modelValue="handleSignedDocumentsChange"
      />
    </div>
    <div class="d-flex justify-center">
      <app-button
        class="btn-search"
        type="outlined"
        variant="neutral"
        is-icon-color-custom
        :disabled="loading"
        @click="submit"
      >
        <template #left-icon>
          <app-material-icon name="search" />
        </template>
        {{ $t("archives.ArchivesSearchArea.btnSearch") }}
      </app-button>
    </div>
  </div>
</template>

<script>
import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import AppHelpTooltip from '@/common/AppHelpTooltip.vue'
import AppTextField from '@/common/AppTextField.vue'
import { getRelativePastDateRange } from '@/common/utils/dates'
import { ARCHIVES_ADD_ROUTE_NAME } from '@/router/routerData'

export default {
  name: 'ArchivesSearchArea',
  components: {
    AppDateRangePicker,
    AppHelpTooltip,
    AppTextField,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    additionalFields: {
      type: Array,
      default: () => [],
    },
  },
  data () {
    return {
      ARCHIVES_ADD_ROUTE_NAME,
      search: {
        docName: '',
        folderRef: '',
        archiverName: '',
        archiveDate: {
          begin: '',
          end: '',
        },
        signerName: '',
        signDate: {
          begin: '',
          end: '',
        },
        signedDocuments: false,
        additionalFields: {},
      },
      archiveDateModel: [],
      signDateModel: [],
    }
  },
  mounted () {
    this.search.archiveDate = getRelativePastDateRange(3, 'month')
    this.archiveDateModel = [this.search.archiveDate.begin, this.search.archiveDate.end]
    this.submit(this.search)
  },
  methods: {
    submit () {
      this.$emit('submit', this.search)
    },
    handleSignedDocumentsChange (value) {
      if (!value) {
        this.search.signDate = {
          begin: '',
          end: '',
        }
        this.signDateModel = []
        this.search.signerName = ''
      }
      this.search.signedDocuments = value
    },
    handleDateRangeChange (value, field) {
      this[field + 'Model'] = value
      this.search[field].begin = value[0]
      if (value.length === 2) {
        this.search[field].end = value[1]
      }
    },
  },
}
</script>
<style scoped lang="scss">
@import "@/scss/border-tokens";

.search-area-container {
  border: get-border();
}
</style>
